import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { shallowEqual, useDispatch } from 'react-redux';
import { withRouter } from 'react-router-dom';

import { stringHelper } from '@zg-rentals/rental-manager-env';
import { parseQueryString } from '@zg-rentals/string-utils';
import { Box, Page, Paragraph, Tabs, TextButton, Toast, useToast } from '@zillow/constellation-10';
import { IconChevronLeftFilled } from '@zillow/constellation-icons';

import { DashboardContext } from './context';
import { FreemiumContext } from '../../components/Freemium/context';
import { RMWContext } from '../../providers/RMWContext';

import { RoutingSelectors } from '../../selectors';
import { useSelector } from '../../selectors/useSelector';

import freemiumActions from '../../components/Freemium/actions';
import PropertiesActions from '../../actions/PropertiesActions';

import { default as dashboardActions } from './actions';

import DashboardCSEvents from '../../model/ClickstreamEvent/DashboardCSEvents';

import PropertyConstants from '../../constants/PropertyConstants';
import ErrorMessageConstants from '../../constants/ErrorMessageConstants';

import { RMW_PATH, RMW_SUB_PATH } from '../../pathsManifest';

import { useGetPropertyDetailsOverview } from '../../hooks/queries/useGetPropertyDetailsOverview';
import { syncCurrentPropertyRestrictions, useGetRestrictions } from '../../hooks/queries/properties';
import { useUpdateSecurityDepositAndRentPrice } from '../../hooks/queries/rentalProtection';

import numberUtils from '../../utils/numberUtils';
import OverviewPage from '../OverviewPage';

import AuthCheckWrapper from '../AuthCheckWrapper';
import ConnectedInterestedRenters from '../../components/ConnectedInterestedRenters';
import ConnectedLeaseAndPropertyConditionModule from '../../components/ConnectedLeaseAndPropertyConditionModule';
import ConnectedListing from '../../components/ConnectedListing';
import ConnectedPaymentsModule from '../../components/ConnectedPaymentsModule';
import FeaturesOfPremium from '../../components/Freemium/components/FeaturesOfPremium';
import HeadWrapper from '../../components/HeadWrapper';

import SecurityDepositUpdateOnRentGuaranteeDropModal from '../../components/Modal/SecurityDepositUpdateOnRentGuaranteeDropModal';
import SecurityDepositUpdateFailureModal from '../../components/Modal/SecurityDepositUpdateFailureModal';
import ReviewSecurityDepositModal from '../../components/ConnectedPropertyCard/components/ReviewSecurityDepositModal';
import InviteToApplyModal from '../../components/Modal/InviteToApplyModal';

import PropertyCard from './components/PropertyCard';
import Navigation from './components/Navigation';

import RentGuaranteeBannerMultiPolicy from '../../components/ConnectedPropertyCard/components/RentGuaranteeBannerMultiPolicy';

import StickyContainer from '../../components/StickyContainer';

import { TabPanel } from './styles';
import ConnectedMaintenanceModule from '../../components/ConnectedMaintenanceModule';
import RestrictedListingBanner from '../../components/RestrictedListingBanner';

import type { State as DashboardState } from './reducer';

type Location = {
  pathname: string;
  search: string;
};

type Props = {
  location?: Location;
  match?: {
    url: string;
  };
};

export const NAV_ITEM_TITLE = {
  DASHBOARD_APPLICATIONS: 'Applications',
  DASHBOARD_LEASES: 'Leases',
  DASHBOARD_LISTING: 'Listing',
  DASHBOARD_OVERVIEW: 'Overview',
  DASHBOARD_PAYMENTS: 'Payments',
  DASHBOARD_MAINTENANCE_TASKS: 'Maintenance',
};

const TAB_PAGE_TITLES = {
  [RMW_PATH.DASHBOARD_OVERVIEW]: NAV_ITEM_TITLE.DASHBOARD_OVERVIEW,
  [RMW_PATH.DASHBOARD_LISTING]: NAV_ITEM_TITLE.DASHBOARD_LISTING,
  [RMW_PATH.DASHBOARD_APPLICATIONS]: NAV_ITEM_TITLE.DASHBOARD_APPLICATIONS,
  [RMW_PATH.DASHBOARD_LEASES]: NAV_ITEM_TITLE.DASHBOARD_LEASES,
  [RMW_PATH.DASHBOARD_PAYMENTS]: NAV_ITEM_TITLE.DASHBOARD_PAYMENTS,
  [RMW_PATH.DASHBOARD_MAINTENANCE_TASKS]: NAV_ITEM_TITLE.DASHBOARD_MAINTENANCE_TASKS,
};

const DASHBOARD_FETCH_TIMEOUT = 3000;

/**
 * Get a boolean that sets the value for the fluid prop for Page Content
 * on screen dimensions less than or equal to sm
 *
 * @param activePath string
 * @returns boolean
 */
const getFluidByActivePath = ({ activePath }: { activePath: string }) => {
  if (
    activePath === RMW_PATH.DASHBOARD_LISTING ||
    activePath === RMW_PATH.DASHBOARD_LEASES ||
    activePath === RMW_PATH.DASHBOARD_PAYMENTS
  ) {
    return true;
  }

  return false;
};

const DashboardPage = ({ location = { pathname: '', search: '' } }: Props) => {
  const dispatch = useDispatch();
  const { enqueueToast } = useToast();

  const { dashboardState, reducerDispatch: dashboardReducerDispatch } = useContext(DashboardContext);
  const {
    // @ts-expect-error
    freemiumReducerDispatch,
  } = useContext(FreemiumContext);
  const { paths } = useContext(RMWContext);

  const listingAlias = useSelector((state) => RoutingSelectors.makeListingId(state));
  const {
    active,
    floorplans,
    isEnabled: isFreemiumEnabled,
    isRentalProtectionEligible,
    type: activeProductType,
    user,
    shouldShowRestrictionsBanner,
    propertyName,
    propertyStreet,
  } = useSelector((state) => {
    return {
      active: state.properties.currentProperty.active,
      floorplans: state.properties.currentProperty.floorplans,
      isEnabled: state.properties.currentProperty.freemium.isEnabled,
      isRentalProtectionEligible: state.properties.currentProperty.rentalProtection.isEligible,
      type: state.properties.currentProperty.freemium.activeProduct.type,
      user: state.user,
      shouldShowRestrictionsBanner: state.properties.currentProperty.updateActiveStatus.showRestrictionOnDashboard,
      propertyName: state.properties.currentProperty.name,
      propertyStreet: state.properties.currentProperty.street,
    };
  }, shallowEqual);

  const { pathname } = location;

  const [activePath, setActivePath] = useState(
    `${RMW_SUB_PATH.DASHBOARD}/${stringHelper.getLastPieceFromPath(pathname)}`,
  );
  const [isLoadingComplete, setIsLoadingComplete] = useState<boolean>(false);

  // rental protection
  const [showSecurityDepositUpdateModal, setShowSecurityDepositUpdateModal] = useState(false);
  const [showSecurityDepositUpdateFailureModal, setShowSecurityDepositUpdateFailureModal] = useState(false);
  const [showReviewSecurityDepositModalOpen, setShowReviewSecurityDepositModalOpen] = useState(false);
  const [securityDepositUpdateModalProps, setSecurityDepositUpdateModalProps] = useState<{
    securityDeposit: string;
    rentAmount: string;
    selectedOption: string;
  } | null>(null);

  const featuresOfPremiumRef = useRef<HTMLDivElement>(null);
  const prevActivePath = useRef('');

  const navBarRef = useRef<HTMLDivElement>(null);

  const { error } = useGetPropertyDetailsOverview(listingAlias);

  const { data: restrictionsData } = useGetRestrictions(listingAlias);

  const { mutateAsync: updateSecurityDepositAndRentPrice } = useUpdateSecurityDepositAndRentPrice();

  const {
    fluidSmallLTE: dashboardFluidSmallLTE = false,
    isFetching: isDashboardStateFetching,
  }: Partial<DashboardState> = dashboardState;

  const displayFeaturesOfPremium =
    isFreemiumEnabled &&
    activePath === RMW_PATH.DASHBOARD_LISTING &&
    (active || activeProductType === PropertyConstants.PRODUCT_TYPE_NAME_TO_ID.PREMIUM);

  const DASHBOARD_PATH = {
    applications: `${paths.propertiesPage}/${listingAlias}/${RMW_PATH.DASHBOARD_APPLICATIONS}`,
    leases: `${paths.propertiesPage}/${listingAlias}/${RMW_PATH.DASHBOARD_LEASES}`,
    payments: `${paths.propertiesPage}/${listingAlias}/${RMW_PATH.DASHBOARD_PAYMENTS}`,
    listing: `${paths.propertiesPage}/${listingAlias}/${RMW_PATH.DASHBOARD_LISTING}`,
    overview: `${paths.propertiesPage}/${listingAlias}/${RMW_PATH.DASHBOARD_OVERVIEW}`,
    maintenanceTasks: `${paths.propertiesPage}/${listingAlias}/${RMW_PATH.DASHBOARD_MAINTENANCE_TASKS}`,
  };

  const RMW_PATH_MAP = {
    [RMW_PATH.DASHBOARD_APPLICATIONS]: DASHBOARD_PATH.applications,
    [RMW_PATH.DASHBOARD_LEASES]: DASHBOARD_PATH.leases,
    [RMW_PATH.DASHBOARD_OVERVIEW]: DASHBOARD_PATH.overview,
    [RMW_PATH.DASHBOARD_LISTING]: DASHBOARD_PATH.listing,
    [RMW_PATH.DASHBOARD_PAYMENTS]: DASHBOARD_PATH.payments,
    [RMW_PATH.DASHBOARD_MAINTENANCE_TASKS]: DASHBOARD_PATH.maintenanceTasks,
  };

  const floorPlanId = floorplans[0]?.floorPlanId;

  // per design there is no margin bottom for the overview tab
  const marginBottomHeaderTabsDefaultValue =
    activePath !== RMW_PATH.DASHBOARD_OVERVIEW ? { base: '800', lg: '1000' } : 0;

  const shouldDisplayBanner = isRentalProtectionEligible && activePath === RMW_PATH.DASHBOARD_OVERVIEW;
  const pageBackgroundColorName = activePath === RMW_PATH.DASHBOARD_OVERVIEW ? 'bg.softest' : 'white';

  useEffect(() => {
    const fluidSmallLTE = getFluidByActivePath({ activePath });

    // update dashboard context
    // the child components will add conditionals to hide or show borders
    dashboardActions.updateAnyProperty({
      reducerDispatch: dashboardReducerDispatch,
      data: {
        fluidSmallLTE,
        activePath,
      },
    });
  }, [activePath]);

  useEffect(() => {
    if (restrictionsData) {
      syncCurrentPropertyRestrictions({ data: restrictionsData, dispatch });
    }
  }, [restrictionsData]);

  useEffect(() => {
    if (isDashboardStateFetching) {
      setTimeout(() => {
        dashboardActions.updateAnyProperty({
          reducerDispatch: dashboardReducerDispatch,
          data: {
            isFetching: false,
          },
        });
      }, DASHBOARD_FETCH_TIMEOUT);
    }
  }, [isDashboardStateFetching]);

  useEffect(() => {
    if (featuresOfPremiumRef) {
      setFeaturesOfPremiumRef();
    }
  }, [featuresOfPremiumRef]);

  useEffect(() => {
    prevActivePath.current = activePath;
    const lastPiece = stringHelper.getLastPieceFromPath(pathname) || '';
    const updatedActivePath = `${RMW_SUB_PATH.DASHBOARD}/${lastPiece}`;

    setActivePath(updatedActivePath);
    DashboardCSEvents.ViewDashboardTab({ user, tab: lastPiece });
  }, [pathname]);

  useEffect(() => {
    checkForRentalProtectionQueryParams();
  }, []);

  /**
   * sets a ref to the features of premium information
   * this is used in an outside component to scroll to the features of premium section
   */
  const setFeaturesOfPremiumRef = () => {
    freemiumActions.updateData({
      reducerDispatch: freemiumReducerDispatch,
      data: {
        featuresOfPremiumRef,
      },
    });
  };

  const checkForRentalProtectionQueryParams = () => {
    if (location?.search?.includes('rentalProtectionPolicyDropped=true')) {
      enqueueToast(<Toast tone="success">You dropped out of Rental Protection request</Toast>);
    }

    if (location?.search?.includes('updateDepositOnPolicyDrop')) {
      const { rentAmount, securityDeposit, selectedOption } = parseQueryString(location.search);

      setSecurityDepositUpdateModalProps({
        securityDeposit,
        rentAmount,
        selectedOption,
      });

      setShowSecurityDepositUpdateModal(true);
    }

    if (location?.search?.includes('reviewSecurityDeposit')) {
      setShowReviewSecurityDepositModalOpen(true);
    }
  };

  const closeSecurityDepositUpdateModal = () => {
    setShowSecurityDepositUpdateModal(false);
  };

  const closeReviewSecurityDepositModal = () => {
    setShowReviewSecurityDepositModalOpen(false);
  };

  const closeSecurityDepositUpdateFailureModal = () => {
    setShowSecurityDepositUpdateFailureModal(false);
  };

  const handleLoadComplete = useCallback(() => {
    setIsLoadingComplete(true);
  }, []);

  const handleSelected = useCallback(
    (id: string) => {
      const prevScrollPos = window.scrollY;
      let scrollPos;
      let navBarOffsetTop = navBarRef.current?.offsetTop;
      if (navBarOffsetTop) {
        navBarOffsetTop -= 16;
        scrollPos = prevScrollPos >= navBarOffsetTop ? navBarOffsetTop : prevScrollPos;
      }

      if (RMW_PATH_MAP[id]) {
        window.router.transitionTo(RMW_PATH_MAP[id], {}, scrollPos);
      }
    },
    [listingAlias],
  );

  const handleSecurityDepositUpdate = (
    updatedDeposit: string,
    updatedRentAmount: string,
    selectedPaymentOption: string,
  ) => {
    const updatePromise = selectedPaymentOption
      ? updateSecurityDepositAndRentPrice({
          listingAlias,
          securityDeposit: updatedDeposit,
          floorPlanId,
          rentPrice: updatedRentAmount,
        })
      : dispatch(
          PropertiesActions.updateSecurityDepositForListing(
            listingAlias,
            numberUtils.asCurrency(Number(updatedDeposit), '0.00'),
          ),
        );

    updatePromise
      // @ts-expect-error
      .then(() => {
        enqueueToast(<Toast tone="success">You updated your security deposit</Toast>);
        setShowSecurityDepositUpdateFailureModal(false);
      })
      .catch(() => {
        setShowSecurityDepositUpdateFailureModal(true);
        setShowSecurityDepositUpdateModal(false);
      });
  };

  const renderPageContent = () => {
    return (
      <>
        <TabPanel value={RMW_PATH.DASHBOARD_OVERVIEW}>
          <OverviewPage />
        </TabPanel>
        <TabPanel value={RMW_PATH.DASHBOARD_LISTING}>
          <ConnectedListing />
        </TabPanel>
        <TabPanel value={RMW_PATH.DASHBOARD_APPLICATIONS}>
          <ConnectedInterestedRenters />
        </TabPanel>
        <TabPanel value={RMW_PATH.DASHBOARD_LEASES}>
          <ConnectedLeaseAndPropertyConditionModule />
        </TabPanel>
        <TabPanel value={RMW_PATH.DASHBOARD_PAYMENTS}>
          <ConnectedPaymentsModule />
        </TabPanel>
        <TabPanel value={RMW_PATH.DASHBOARD_MAINTENANCE_TASKS}>
          <ConnectedMaintenanceModule />
        </TabPanel>
      </>
    );
  };

  return (
    <AuthCheckWrapper>
      <Page.Root
        data-testid="DashboardPage"
        css={{ backgroundColor: pageBackgroundColorName }}
        data-eventid={DashboardCSEvents.ViewDashboardTab.data_event_id}
        data-eventdescription="View dashboard tab event"
      >
        {error ? (
          <Box css={{ height: '100vh' }}>
            <Page.Breadcrumb>
              <TextButton asChild={true} icon={<IconChevronLeftFilled />}>
                <a href={paths.propertiesPage}>Your properties</a>
              </TextButton>
            </Page.Breadcrumb>
            <Page.Content>
              <Paragraph>
                {/* @ts-expect-error */}
                {error?.error?.httpStatus === '403'
                  ? ErrorMessageConstants.LISTING_NO_PERMISSION
                  : ErrorMessageConstants.DEFAULT_LISTING_ERROR}
              </Paragraph>
            </Page.Content>
          </Box>
        ) : (
          <>
            <HeadWrapper
              title={`${TAB_PAGE_TITLES[activePath]} - ${propertyName || propertyStreet} | Zillow Rental Manager`}
            />
            <Tabs.Root selected={activePath} onSelectedChange={handleSelected}>
              {isLoadingComplete && (
                <>{shouldShowRestrictionsBanner && <RestrictedListingBanner isFromDashboard={true} />}</>
              )}

              <Box
                data-testid="DashboardHeader"
                css={{
                  backgroundColor: 'bg.neutral',
                  borderBottom: '1px solid {colors.Tabs.default.border.tab.default}',
                }}
              >
                <Page.Content
                  css={{
                    marginBlock: '0',
                    paddingTop: { base: '400', md: '800' },
                    paddingInline: 0,
                  }}
                >
                  <Box css={{ marginInline: '400' }}>
                    <PropertyCard listingAlias={listingAlias} onLoadComplete={handleLoadComplete} />
                  </Box>

                  {/* adding box here and within StickyContainer to account for margin when nav is sticky */}
                  <Box css={{ marginBottom: '600' }} ref={navBarRef} />
                  <StickyContainer
                    addBorderBottom={true}
                    passDownFixedStatus={true}
                    offsetForLeftNav={true}
                    fixedOuterContainerStyles={{ height: 64, boxSizing: 'content-box' }}
                    isLoading={!isLoadingComplete}
                  >
                    <Box css={{ marginBottom: '400' }} />
                    <Navigation activePath={activePath} dashboardPathObj={DASHBOARD_PATH} />
                  </StickyContainer>
                </Page.Content>
              </Box>
              {isLoadingComplete && shouldDisplayBanner && (
                <div data-testid="BannerContainer">
                  <RentGuaranteeBannerMultiPolicy />
                </div>
              )}
              <Box
                data-testid="HeaderBottomMargin"
                css={{ marginBottom: { base: marginBottomHeaderTabsDefaultValue, xl: '1000' } }}
              />
              <Page.Content
                data-testid="DashboardPageContent"
                css={{
                  marginTop: '0',
                  mdDown: { padding: dashboardFluidSmallLTE ? '0' : undefined },
                  lg: { marginBottom: '1000' },
                }}
              >
                {renderPageContent()}
              </Page.Content>
            </Tabs.Root>

            {/* need to set margin larger because component has an offset via StyledMobileDiv */}
            {isLoadingComplete && displayFeaturesOfPremium && (
              <Box css={{ marginTop: { base: '1200', xl: '1000' } }} ref={featuresOfPremiumRef}>
                <FeaturesOfPremium />
              </Box>
            )}

            {showSecurityDepositUpdateModal && (
              // @ts-expect-error
              <SecurityDepositUpdateOnRentGuaranteeDropModal
                close={closeSecurityDepositUpdateModal}
                handleSecurityDepositUpdate={handleSecurityDepositUpdate}
                listingAlias={listingAlias}
                {...securityDepositUpdateModalProps}
              />
            )}

            {showSecurityDepositUpdateFailureModal && (
              <SecurityDepositUpdateFailureModal close={closeSecurityDepositUpdateFailureModal} />
            )}

            {showReviewSecurityDepositModalOpen && (
              <ReviewSecurityDepositModal onClose={closeReviewSecurityDepositModal} listingAlias={listingAlias} />
            )}

            <InviteToApplyModal />
          </>
        )}
      </Page.Root>
    </AuthCheckWrapper>
  );
};

export default withRouter(DashboardPage);
